import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { evaService } from '../../../services/evaService';

const BAD_STATUSES = new Set(['requiere_atencion', 'critico', 'malo']);

const STATUS_LABEL = {
  critico: 'Crítico',
  requiere_atencion: 'Mal Estado',
  malo: 'Mal Estado',
  bueno: 'Bueno',
};

const STATUS_COLOR = {
  critico: 'text-red-600 bg-red-50 border-red-200',
  requiere_atencion: 'text-yellow-600 bg-yellow-50 border-yellow-200',
  malo: 'text-yellow-600 bg-yellow-50 border-yellow-200',
  bueno: 'text-green-600 bg-green-50 border-green-200',
};

const InspectionSuggestions = ({
  suggestions = [],
  selectedSuggestions = [],
  onToggleSuggestion,
  inspectionFindings = [],
  vehicle = null,
}) => {
  const [evaPartTypes, setEvaPartTypes] = useState({});
  const [loadingEva, setLoadingEva] = useState(false);

  // Filter to only bad/critical findings
  const badFindings = inspectionFindings?.filter(f => BAD_STATUSES?.has(f?.status));

  // Use EVA to identify part types by VIN when we have vehicle + bad findings
  useEffect(() => {
    const fetchEvaPartTypes = async () => {
      if (!vehicle?.vin && !vehicle?.marca) return;
      if (badFindings?.length === 0) return;

      // Build hallazgos list from parts + findings
      const hallazgos = [];
      badFindings?.forEach(f => {
        if (f?.parts?.length > 0) {
          f?.parts?.forEach(p => {
            hallazgos?.push({ categoria: f?.category, descripcion: p?.part_description });
          });
        } else if (f?.refaccion) {
          hallazgos?.push({ categoria: f?.category, descripcion: f?.refaccion });
        } else if (f?.findings) {
          hallazgos?.push({ categoria: f?.category, descripcion: f?.findings });
        }
      });

      if (hallazgos?.length === 0) return;

      setLoadingEva(true);
      try {
        const result = await evaService?.generarPresupuesto({
          cliente: { nombre: 'Taller' },
          vehiculo: {
            vin: vehicle?.vin,
            marca: vehicle?.marca,
            modelo: vehicle?.modelo,
            year: vehicle?.year,
            motor: vehicle?.motor,
          },
          hallazgos,
          modulo: 'Cotización',
        });

        // Map EVA partidas to a lookup by description
        if (result?.partidas_sugeridas?.length > 0) {
          const lookup = {};
          result?.partidas_sugeridas?.forEach(p => {
            lookup[p?.descripcion?.toLowerCase()] = p?.tipo;
          });
          setEvaPartTypes(lookup);
        }
      } catch (err) {
        console.warn('[InspectionSuggestions] EVA part type lookup failed:', err?.message);
      } finally {
        setLoadingEva(false);
      }
    };

    fetchEvaPartTypes();
  }, [vehicle?.vin, badFindings?.length]);

  const getEvaPartType = (description) => {
    if (!description) return null;
    return evaPartTypes?.[description?.toLowerCase()] || null;
  };

  const getPartTypeLabel = (tipo) => {
    switch (tipo) {
      case 'refaccion': return { label: 'Refacción', color: 'text-blue-600 bg-blue-50 border-blue-200' };
      case 'mano_obra': return { label: 'Mano de Obra', color: 'text-purple-600 bg-purple-50 border-purple-200' };
      case 'llanta': return { label: 'Llanta', color: 'text-orange-600 bg-orange-50 border-orange-200' };
      default: return null;
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-1 p-4 md:p-6">
      <div className="flex items-center gap-3 mb-4 md:mb-6">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Icon name="ClipboardList" size={20} className="text-primary" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
            Diagnóstico de Inspección
          </h2>
          <p className="text-sm caption text-muted-foreground">
            Refacciones y mano de obra diagnosticadas — solo hallazgos en Mal Estado o Crítico
          </p>
        </div>
        {vehicle && (
          <div className="hidden sm:flex flex-col items-end text-right">
            <span className="text-xs font-medium text-foreground">
              {vehicle?.marca} {vehicle?.modelo} {vehicle?.year}
            </span>
            {vehicle?.vin && (
              <span className="text-xs text-muted-foreground font-mono">VIN: {vehicle?.vin}</span>
            )}
            {loadingEva && (
              <span className="text-xs text-primary flex items-center gap-1 mt-0.5">
                <Icon name="Loader2" size={12} className="animate-spin" />
                EVA identificando piezas...
              </span>
            )}
          </div>
        )}
      </div>

      {badFindings?.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="CheckCircle2" size={48} className="mx-auto mb-3 opacity-40 text-success" />
          <p className="text-sm font-medium">Sin hallazgos críticos</p>
          <p className="text-xs mt-1">Solo se muestran hallazgos marcados como Mal Estado o Crítico en la inspección.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {badFindings?.map((finding, idx) => {
            const hasParts = finding?.parts?.length > 0;
            const hasSinglePart = !hasParts && finding?.refaccion;
            const hasLabor = finding?.horas_estimadas > 0 || finding?.requiere_mano_obra;

            return (
              <div
                key={finding?.id || idx}
                className="border border-border rounded-lg overflow-hidden"
              >
                {/* Category header */}
                <div className="flex items-center justify-between px-4 py-3 bg-muted/40 border-b border-border">
                  <div className="flex items-center gap-2">
                    <Icon name="Wrench" size={16} className="text-primary" />
                    <span className="font-medium text-sm text-foreground">{finding?.category}</span>
                  </div>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full border ${
                      STATUS_COLOR?.[finding?.status] || 'text-gray-600 bg-gray-50 border-gray-200'
                    }`}
                  >
                    {STATUS_LABEL?.[finding?.status] || finding?.status}
                  </span>
                </div>

                <div className="p-4 space-y-3">
                  {/* Findings text */}
                  {finding?.findings && (
                    <div className="bg-muted/50 rounded-lg p-3">
                      <p className="text-xs font-medium text-muted-foreground mb-1">Hallazgo del técnico:</p>
                      <p className="text-sm text-foreground">{finding?.findings}</p>
                    </div>
                  )}

                  {/* Recommendations */}
                  {finding?.recommendations && (
                    <div className="bg-blue-50 dark:bg-blue-950/20 rounded-lg p-3">
                      <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">Recomendación:</p>
                      <p className="text-sm text-foreground">{finding?.recommendations}</p>
                    </div>
                  )}

                  {/* Parts from inspection_parts_required (multi-row) */}
                  {hasParts && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                        <Icon name="Package" size={12} />
                        Refacciones requeridas:
                      </p>
                      <div className="space-y-1.5">
                        {finding?.parts?.map((part, pIdx) => {
                          const evaType = getEvaPartType(part?.part_description);
                          const typeInfo = getPartTypeLabel(evaType);
                          return (
                            <div key={part?.id || pIdx} className="flex items-center justify-between bg-background border border-border rounded-md px-3 py-2">
                              <div className="flex items-center gap-2">
                                <Icon name="Box" size={14} className="text-muted-foreground" />
                                <span className="text-sm text-foreground">{part?.part_description}</span>
                                {typeInfo && (
                                  <span className={`text-xs px-1.5 py-0.5 rounded border ${typeInfo?.color}`}>
                                    {typeInfo?.label}
                                  </span>
                                )}
                              </div>
                              <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded">
                                Cant: {part?.quantity || 1}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Single refaccion fallback */}
                  {hasSinglePart && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                        <Icon name="Package" size={12} />
                        Refacción requerida:
                      </p>
                      <div className="flex items-center justify-between bg-background border border-border rounded-md px-3 py-2">
                        <div className="flex items-center gap-2">
                          <Icon name="Box" size={14} className="text-muted-foreground" />
                          <span className="text-sm text-foreground">{finding?.refaccion}</span>
                          {(() => {
                            const evaType = getEvaPartType(finding?.refaccion);
                            const typeInfo = getPartTypeLabel(evaType);
                            return typeInfo ? (
                              <span className={`text-xs px-1.5 py-0.5 rounded border ${typeInfo?.color}`}>
                                {typeInfo?.label}
                              </span>
                            ) : null;
                          })()}
                        </div>
                        <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded">
                          Cant: {finding?.cantidad || 1}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Labor hours */}
                  {hasLabor && (
                    <div className="flex items-center justify-between bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800 rounded-md px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Icon name="Clock" size={14} className="text-purple-600" />
                        <span className="text-sm text-foreground">Mano de obra — {finding?.category}</span>
                        <span className="text-xs px-1.5 py-0.5 rounded border text-purple-600 bg-purple-50 border-purple-200">
                          Mano de Obra
                        </span>
                      </div>
                      <span className="text-xs font-medium text-purple-600 bg-purple-100 px-2 py-0.5 rounded">
                        {finding?.horas_estimadas || 1} hr{(finding?.horas_estimadas || 1) !== 1 ? 's' : ''}
                      </span>
                    </div>
                  )}

                  {/* No parts/labor captured */}
                  {!hasParts && !hasSinglePart && !hasLabor && (
                    <p className="text-xs text-muted-foreground italic">
                      Sin refacciones ni mano de obra capturadas para este hallazgo.
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InspectionSuggestions;